import { AxiosError } from 'axios';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { filter, find, isEmpty, some } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { assetsApi, fundsRequestsApi, shopsApi, usersApi } from 'api';
import {
  CloseDialogHandler,
  CloseDialogResult,
  DataWrapper,
  Dialog,
  FormControls,
  FormikNumericField,
  FormikSelect,
  FormikTextField,
  UserSelect,
} from 'components';
import {
  AppConfigArea,
  AppConfigKey,
  AssetType,
  Network,
  OperationType,
  QueryKey,
  UserRole,
} from 'enums';
import {
  useAppConfigValue,
  useCurrencies,
  useMutation,
  useShopsQuery,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { CreateFundsRequest } from 'types';
import { assetUtils, fundsRequestUtils, validationUtils } from 'utils';

type Props = {
  open: boolean;
  onClose: CloseDialogHandler;
};

type Values = {
  operationType: OperationType;
  userId: string;
  assetId: string;
  assetCurrencyId: string;
  amount: number;
  comment?: string;
  category?: string;
};

// Admin creates funds requests with no fees. Use this const to match API
const DEFAULT_FEE = 2;

export const CreateFundRequestDialog: React.FC<Props> = ({ open, onClose }) => {
  const { role } = useUser();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.funds_request.create_funds_requests_dialog',
  });
  const queryClient = useQueryClient();
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const queryResultAssets = useQuery(QueryKey.Assets, assetsApi.getAll);
  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );
  const queryResultUsers = useQuery(
    QueryKey.Users,
    usersApi.getAllWithPlatform,
  );

  const categoriesConfigQueryResult = useAppConfigValue({
    area: AppConfigArea.Platform,
    key: AppConfigKey.InternalTransferCategories,
  });

  const {
    getAssetCurrencySymbol,
    assetCurrenciesOptions,
    defaultAssetCurrency,
  } = useCurrencies();

  const [assetIsRequired, setAssetIsRequired] = useState(true);
  const initialValues: Values = useMemo(
    () => ({
      operationType: OperationType.Deposit,
      userId: '',
      assetId: '',
      assetCurrencyId: '',
      amount: 0,
      comment: '',
      category: '',
    }),
    [],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        operationType: Yup.string().required(tCommon('errors.required')),
        category: Yup.string().when('operationType', {
          is: OperationType.Withdrawal,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        userId: Yup.string().required(tCommon('errors.required')),
        ...(assetIsRequired && {
          assetId: Yup.string()
            .required(tCommon('errors.required'))
            .uuid(tCommon('errors.invalid')),
        }),
        assetCurrencyId: Yup.string()
          .required(tCommon('errors.required'))
          .uuid(tCommon('errors.invalid')),
        amount: Yup.number()
          .required(tCommon('errors.required'))
          .positive(tCommon('errors.natural_number')),
        comment: Yup.string().when('operationType', {
          is: OperationType.AdminInsuranceDepositBurn,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [assetIsRequired, tCommon],
  );

  const { mutate: createDeposit, isLoading: isCreateDepositLoading } =
    useMutation(fundsRequestsApi.createDepositForUser, {
      requireSecureConfirm: true,
    });

  const { mutate: createWithDrawal, isLoading: isCreateWithdrawalLoading } =
    useMutation(fundsRequestsApi.createWithdrawalForUser, {
      requireSecureConfirm: true,
    });

  const formatFundsRequest = useCallback(
    (values: Values): CreateFundsRequest => {
      // When the chosen option is a shop with no asset yet, its value carries shop.id
      // instead of an existing assetId, so send it as shopId.
      const selectedRealAsset = find(queryResultAssets.data, {
        id: values.assetId,
      });
      const shopId = selectedRealAsset
        ? undefined
        : find(queryResultShops?.data, { id: values.assetId })?.id;
      return {
        assetId: selectedRealAsset?.id,
        ...(shopId && { shopId }),
        amount: values.amount,
        paymentAmount: values.amount,
        fee: selectedRealAsset?.shop?.withdrawalFeeAmount || DEFAULT_FEE,
        assetCurrencyId: values.assetCurrencyId,
        network: Network.TRC20,
        address: '',
        ...(values.operationType === OperationType.Deposit && { hash: '' }),
        ...(values.operationType ===
          OperationType.AdminInsuranceDepositBurn && {
          operationType: OperationType.AdminInsuranceDepositBurn,
        }),
        ...(values.operationType !== OperationType.Deposit && {
          comment: values.comment,
        }),
        ...(values.operationType === OperationType.Withdrawal && {
          category: values.category,
        }),
      };
    },
    [queryResultAssets.data, queryResultShops?.data],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      const userId = values.userId;
      const fundsRequest = formatFundsRequest(values);

      let apiFn =
        values.operationType === OperationType.Deposit
          ? createDeposit
          : createWithDrawal;

      return apiFn(
        { userId, fundsRequest },
        {
          onSuccess: (data) => {
            formikHelpers.resetForm();
            queryClient.invalidateQueries(QueryKey.Assets);
            onClose({ ok: true, data });
          },
          onSettled: () => formikHelpers.setSubmitting(false),
          onError: (error) => {
            formikHelpers.setErrors(
              validationUtils.getFormErrors(error as AxiosError),
            );
          },
        },
      );
    },
    [formatFundsRequest, queryClient, createDeposit, createWithDrawal, onClose],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });
  const { setFieldValue, setFieldTouched, values, isSubmitting } = formik;

  const isLoading = useMemo(
    () =>
      isSubmitting ||
      queryResultUsers?.isLoading ||
      queryResultShops?.isLoading ||
      isCreateDepositLoading ||
      isCreateWithdrawalLoading,
    [
      isSubmitting,
      queryResultUsers,
      queryResultShops,
      isCreateDepositLoading,
      isCreateWithdrawalLoading,
    ],
  );

  const availableAssets = useMemo(() => {
    const filteredAssets = assetUtils.filterAssets(
      queryResultAssets.data,
      {
        userId: formik.values.userId,
        assetCurrencyId: formik.values.assetCurrencyId,
      },
      queryResultShops?.data,
    );

    if (values.operationType === OperationType.AdminInsuranceDepositBurn) {
      return filter(
        filteredAssets,
        (asset) => asset.type === AssetType.InsuranceDeposit,
      );
    }

    const assets = filter(
      filteredAssets,
      (asset) => asset.type !== AssetType.InsuranceDeposit,
    );

    // A merchant shop may not have a default-currency (USDT) asset yet — offer it as a
    // synthetic option carrying shop.id so the deposit can create the asset on submit.
    const recipient = find(queryResultUsers.data, { id: values.userId });
    if (
      values.operationType === OperationType.Deposit &&
      recipient?.role === UserRole.Merchant &&
      values.assetCurrencyId === defaultAssetCurrency?.id &&
      queryResultShops?.data
    ) {
      assets.push(
        ...assetUtils.getShopAssetsWithDefaults(
          assets,
          queryResultShops.data,
          values.userId,
          values.assetCurrencyId,
        ),
      );
    }

    return assets;
  }, [
    queryResultAssets,
    formik.values,
    values.operationType,
    values.userId,
    values.assetCurrencyId,
    queryResultShops?.data,
    queryResultUsers.data,
    defaultAssetCurrency?.id,
  ]);

  const assetOptions = useMemo(
    () => assetUtils.getAssetOptions(availableAssets, UserRole.Admin),
    [availableAssets],
  );

  const selectedAsset = useMemo(
    () => find(availableAssets, { id: values.assetId }),
    [availableAssets, values.assetId],
  );

  const availableUsers = useMemo(
    () =>
      filter(
        queryResultUsers.data,
        (user) =>
          ![UserRole.Admin, UserRole.Operator, UserRole.TechOperator].includes(
            user.role,
          ),
      ),
    [queryResultUsers?.data],
  );

  const handleClose = useCallback(
    (result: CloseDialogResult, formikHelpers: FormikHelpers<Values>) => {
      if (result.ok && result.data) {
        handleSubmit(result.data, formikHelpers);
      } else {
        formikHelpers.resetForm();
        onClose(result);
      }
    },
    [onClose, handleSubmit],
  );

  const operationTypesOptions = useMemo(
    () =>
      Object.values(OperationType).map((value) => ({
        value,
        label: fundsRequestUtils.getOperationLabel(value),
      })),
    [],
  );

  const categoriesOptions = useMemo(
    () =>
      Object.entries((categoriesConfigQueryResult?.data as any) || {}).map(
        ([value, label]) => ({ value, label: label as string }),
      ),
    [categoriesConfigQueryResult],
  );

  useEffect(() => {
    if (values.userId && values.assetCurrencyId) {
      const userAssetsExist = some(
        queryResultAssets.data,
        (asset) =>
          asset.userId === values.userId &&
          asset.assetCurrencyId === values.assetCurrencyId,
      );
      setAssetIsRequired(
        userAssetsExist ||
          [
            OperationType.Withdrawal,
            OperationType.AdminInsuranceDepositBurn,
          ].includes(values.operationType) ||
          find(queryResultUsers.data, { id: values.userId })?.role ===
            UserRole.Merchant,
      );
      setFieldValue('assetId', '');
      setFieldTouched('assetId', false);
    }
  }, [
    queryResultAssets.data,
    values.userId,
    values.assetCurrencyId,
    values.operationType,
    setFieldValue,
    setFieldTouched,
    queryResultUsers.data,
  ]);

  useEffect(() => {
    if (selectedAsset?.type === AssetType.InsuranceDeposit) {
      setFieldValue('operationType', OperationType.AdminInsuranceDepositBurn);
    }
  }, [selectedAsset, setFieldValue]);

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        onClose={(data) => handleClose(data, formik)}
        okDisabled={isLoading || !formik.isValid}
        data={formik.values}
        title={t('title')}
      >
        <DataWrapper isLoading={isLoading}>
          <Form>
            <FormControls>
              <FormikSelect
                options={operationTypesOptions}
                label={t('operation_type')}
                className="tw-w-full"
                name="operationType"
              />
              {formik.values.operationType === OperationType.Withdrawal && (
                <FormikSelect
                  options={categoriesOptions}
                  label={t('category')}
                  className="tw-w-full"
                  name="category"
                />
              )}
              <UserSelect name="userId" fullWidth users={availableUsers} />
              <FormikSelect
                label={t('asset_currency')}
                name="assetCurrencyId"
                options={assetCurrenciesOptions}
              />
              <FormikSelect
                className="tw-w-full"
                label={t('asset')}
                name="assetId"
                options={assetOptions}
                disabled={
                  isEmpty(assetOptions) ||
                  !assetIsRequired ||
                  !formik.values.userId ||
                  !formik.values.assetCurrencyId
                }
                {...(!assetIsRequired &&
                  formik.values.operationType === OperationType.Deposit && {
                    helperText: t('no_asset_label'),
                  })}
              />
              <FormikNumericField
                label={t('amount')}
                name="amount"
                className="tw-w-full"
                allowNegative={false}
                disabled={assetIsRequired && !formik.values.assetId}
                suffix={getAssetCurrencySymbol(formik.values.assetCurrencyId)}
              />
              {values.operationType !== OperationType.Deposit && (
                <FormikTextField
                  label={t('comment')}
                  name="comment"
                  className="tw-w-full"
                  multiline
                  rows={3}
                  required={
                    values.operationType ===
                    OperationType.AdminInsuranceDepositBurn
                  }
                />
              )}
            </FormControls>
          </Form>
        </DataWrapper>
      </Dialog>
    </FormikProvider>
  );
};
